import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft } from 'lucide-react'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'
import { customerSchema } from '../../schemas/adminSchemas'
import { CustomerForm } from './CustomerForm'

type CustomerFormValues = z.infer<typeof customerSchema>

type CustomerResponse = CustomerFormValues & {
  id: number
  profileImage?: string | null
}

export function CustomerFormPage({ mode }: { mode: 'edit' }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(mode === 'edit')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [profileImage, setProfileImage] = useState<string | null>(null)
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      mobile: '',
      email: '',
      gender: 'Male',
      status: 'Active',
    },
  })

  useEffect(() => {
    if (mode !== 'edit' || !id) return
    api.get(`/admin/customers/${id}`)
      .then(({ data }) => {
        const customer = data.customer as CustomerResponse
        setProfileImage(customer.profileImage || null)
        form.reset({
          name: customer.name || '',
          mobile: customer.mobile || '',
          email: customer.email || '',
          gender: customer.gender || 'Male',
          status: customer.status || 'Active',
        })
      })
      .catch(() => setError('Failed to load customer'))
      .finally(() => setLoading(false))
  }, [form, id, mode])

  const onSubmit = async (values: CustomerFormValues) => {
    setSaving(true)
    setError('')
    try {
      await api.put(`/admin/customers/${id}`, values)
      navigate('/customers')
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      setError(message || 'Failed to save customer')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="py-24 text-center text-stone-400">Loading customer…</div>

  return (
    <div className="space-y-6">
      <PageTitle
        title={`Edit Customer ${id}`}
        subtitle="Update customer account details."
        action={<NavLink to="/customers"><Button icon={ArrowLeft} variant="soft">Back to Customers</Button></NavLink>}
      />
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {profileImage ? (
        <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-leaf-dark">Profile Image</h2>
          <img
            src={profileImage}
            alt="Customer profile"
            className="mt-4 h-28 w-28 rounded-xl object-cover"
          />
        </section>
      ) : null}
      <CustomerForm form={form} submitLabel={saving ? 'Saving…' : 'Save Customer'} onSubmit={onSubmit} />
    </div>
  )
}
